/**
 * TestDetailsStep — Create Test Wizard (merged Version Selection + Test Details)
 * Step 6F Green Phase
 */

import React, { useEffect, useRef, useState } from "react";
import { apiClient } from "../../services/apiClient";
import { useVersions } from "../../hooks/useVersions";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Combobox } from "../../components/Combobox";
import type { WizardStepProps } from "./VersionSelectionStep";

interface ClientCompany {
  id: string;
  name: string;
}
interface ClientManager {
  id: string;
  name?: string;
  email: string;
  roles?: string[];
}

export function TestDetailsStep({ wizardData, onNext, onBack }: WizardStepProps) {
  const { data: versions } = useVersions();
  const activeVersions = (versions ?? []).filter((v) => v.state === "Active");
  const versionOptions = activeVersions.map((v) => ({ value: v.id, label: v.version_name }));

  const scenario = Number(wizardData?.scenario) || 1;

  const [selectedVersionId, setSelectedVersionId] = useState<string>((wizardData?.content_version_id as string) ?? "");
  const [testName, setTestName] = useState<string>((wizardData?.name as string) ?? "");
  const [dueDate, setDueDate] = useState<string>((wizardData?.due_date as string) ?? "");
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>((wizardData?.client_company_id as string) ?? "");
  const [selectedManagerId, setSelectedManagerId] = useState<string>((wizardData?.client_manager_id as string) ?? "");
  const [isBaselineSource, setIsBaselineSource] = useState<boolean>((wizardData?.is_baseline_source as boolean) ?? false);
  const [baselineName, setBaselineName] = useState<string>((wizardData?.baseline_name as string) ?? "");
  const [companies, setCompanies] = useState<ClientCompany[]>([]);
  const [managers, setManagers] = useState<ClientManager[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<ClientCompany[]>("/api/client-companies")
      .then((data) => {
        if (!cancelled) setCompanies(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (selectedCompanyId) {
      apiClient
        .get<ClientManager[]>(`/api/client-companies/${selectedCompanyId}/managers`)
        .then((data) => {
          if (!cancelled) setManagers(data);
        })
        .catch(() => {
          if (!cancelled) setManagers([]);
        });
    } else {
      apiClient
        .get<ClientManager[]>("/api/client-managers")
        .then((data) => {
          if (!cancelled) setManagers(data);
        })
        .catch(() => {
          if (!cancelled) setManagers([]);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [selectedCompanyId]);

  const today = new Date().toISOString().split("T")[0];

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!selectedVersionId) errs.version = "Content version is required";
    if (!testName.trim()) errs.name = "Test name is required";
    if (!selectedCompanyId) errs.client_company = "Client company is required";
    if (!selectedManagerId) errs.client_manager = "Client manager is required";
    if (!dueDate) {
      errs.due_date = "Due date is required";
    } else if (dueDate < today) {
      errs.due_date = "Due date must be a future date";
    }
    if (scenario === 2 && isBaselineSource && !baselineName.trim()) {
      errs.baseline_name = "Baseline name is required";
    }
    return errs;
  };

  const handleNext = () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      if (errs.name && nameRef.current) nameRef.current.focus();
      return;
    }
    const selectedVersion = activeVersions.find((v) => v.id === selectedVersionId);
    onNext?.({
      content_version_id: selectedVersionId,
      version_name: selectedVersion?.version_name ?? "",
      name: testName,
      due_date: dueDate,
      client_company_id: selectedCompanyId,
      client_manager_id: selectedManagerId,
      is_baseline_source: isBaselineSource,
      baseline_name: baselineName,
    });
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Test Details</h2>
      <p className="text-sm text-brand-steel mb-6">Select the content version and provide core test information.</p>

      <Card className="border-brand-steel/50 shadow-sm mb-6">
        <CardContent className="py-6 px-8">
          <div className="space-y-4">
            {/* Full width: Test Name */}
            <div>
              <label htmlFor="test-name" className="block text-sm font-medium text-gray-700 mb-1">
                Test Name <span className="text-brand-crimson">*</span>
              </label>
              <input
                id="test-name"
                ref={nameRef}
                type="text"
                value={testName}
                onChange={(e) => {
                  setTestName(e.target.value);
                  setErrors((p) => ({ ...p, name: "" }));
                }}
                className="w-full h-9 border border-brand-steel/50 bg-white rounded-lg px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-crimson/30 focus:border-brand-crimson"
                aria-invalid={Boolean(errors.name)}
                aria-describedby={errors.name ? "test-name-error" : undefined}
                placeholder="e.g. Q1 Leadership Assessment"
              />
              {errors.name && (
                <p id="test-name-error" role="alert" className="text-xs text-brand-error mt-1">
                  {errors.name}
                </p>
              )}
            </div>

            {/* Full width: Content Version */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Content Version <span className="text-brand-crimson">*</span>
              </label>
              <Combobox
                options={versionOptions}
                value={selectedVersionId}
                onChange={(val) => {
                  setSelectedVersionId(val);
                  setErrors((p) => ({ ...p, version: "" }));
                }}
                placeholder="Select version..."
                searchPlaceholder="Search versions..."
                className="w-full"
              />
              {errors.version && (
                <p role="alert" className="text-xs text-brand-error mt-1">
                  {errors.version}
                </p>
              )}
            </div>

            {/* 2-column row: Client Company + Client Manager */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client Company <span className="text-brand-crimson">*</span>
                </label>
                <Combobox
                  options={companies.map((c) => ({ value: c.id, label: c.name }))}
                  value={selectedCompanyId}
                  onChange={(val) => {
                    setSelectedCompanyId(val);
                    setSelectedManagerId("");
                    setErrors((p) => ({ ...p, client_company: "", client_manager: "" }));
                  }}
                  placeholder="Select company..."
                  searchPlaceholder="Search companies..."
                  className="w-full"
                />
                {errors.client_company && (
                  <p role="alert" className="text-xs text-brand-error mt-1">
                    {errors.client_company}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client Manager <span className="text-brand-crimson">*</span>
                </label>
                <Combobox
                  options={managers.map((m) => ({ value: m.id, label: m.name ?? m.email }))}
                  value={selectedManagerId}
                  onChange={(val) => {
                    setSelectedManagerId(val);
                    setErrors((p) => ({ ...p, client_manager: "" }));
                  }}
                  placeholder="Select manager..."
                  searchPlaceholder="Search managers..."
                  className="w-full"
                />
                {errors.client_manager && (
                  <p role="alert" className="text-xs text-brand-error mt-1">
                    {errors.client_manager}
                  </p>
                )}
              </div>
            </div>

            {/* Due Date on its own row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="due-date" className="block text-sm font-medium text-gray-700 mb-1">
                  Due Date <span className="text-brand-crimson">*</span>
                </label>
                <input
                  id="due-date"
                  type="date"
                  value={dueDate}
                  onChange={(e) => {
                    setDueDate(e.target.value);
                    setErrors((p) => ({ ...p, due_date: "" }));
                  }}
                  className="w-full h-9 border border-brand-steel/50 bg-white rounded-lg px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-crimson/30 focus:border-brand-crimson"
                  aria-invalid={Boolean(errors.due_date)}
                  min={today}
                />
                {errors.due_date && (
                  <p role="alert" className="text-xs text-brand-error mt-1">
                    {errors.due_date}
                  </p>
                )}
              </div>
            </div>

            {/* Baseline Source — Scenario 2 only */}
            {scenario === 2 && (
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <input
                    id="baseline-source"
                    type="checkbox"
                    checked={isBaselineSource}
                    onChange={(e) => setIsBaselineSource(e.target.checked)}
                    className="rounded border-brand-steel/50"
                    aria-label="Mark as Baseline Source"
                  />
                  <label htmlFor="baseline-source" className="text-sm text-gray-700">
                    Mark as Baseline Source
                  </label>
                </div>
                {isBaselineSource && (
                  <div>
                    <label htmlFor="baseline-name" className="block text-sm font-medium text-gray-700 mb-1">
                      Baseline Name <span className="text-brand-crimson">*</span>
                    </label>
                    <input
                      id="baseline-name"
                      type="text"
                      value={baselineName}
                      onChange={(e) => {
                        setBaselineName(e.target.value);
                        setErrors((p) => ({ ...p, baseline_name: "" }));
                      }}
                      className="w-full h-9 border border-brand-steel/50 bg-white rounded-lg px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-crimson/30 focus:border-brand-crimson"
                      aria-invalid={Boolean(errors.baseline_name)}
                      placeholder="e.g. Senior Leadership 2025"
                    />
                    {errors.baseline_name && (
                      <p id="baseline-name-error" role="alert" className="text-xs text-brand-error mt-1">
                        {errors.baseline_name}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <div>
          {onBack && (
            <Button type="button" variant="outline" onClick={() => onBack({ name: testName, due_date: dueDate, client_company_id: selectedCompanyId, client_manager_id: selectedManagerId, is_baseline_source: isBaselineSource, baseline_name: baselineName })} className="min-w-25 border-brand-steel/40">
              Back
            </Button>
          )}
        </div>
        <Button
          type="button"
          onClick={handleNext}
          className="min-w-25 bg-brand-crimson hover:bg-brand-crimson/90 text-white font-medium text-sm rounded-lg px-6 py-2"
        >
          Next
        </Button>
      </div>
    </div>
  );
}

export default TestDetailsStep;
